import { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BannerService } from "@services/banners/bannerService";
import type { Banner } from "@services/banners/commands/cmdListBanners";
import Message from "@/components/common/Message";
import Loader from "@/components/common/loader/loader";
import showAlert from "src/shared/utils/generic.tsx";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { Paths } from "@/paths";
import LiveNowCard from "./components/LiveNowCard";
import BannersTable from "./components/BannersTable";
import TurnOffBannerModal from "./components/TurnOffBannerModal";
import ConfirmBannerModal from "./components/ConfirmBannerModal";
import { bannerEditPath } from "./bannerForm";
import "./banners.scss";

const bannerService = new BannerService();

/** Admin > Banners (MPN-2265): the Live banner and every banner, behind marketing-banners. */
export default function BannersPage() {
    useDocumentTitle("Banners");
    const navigate = useNavigate();
    const [banners, setBanners] = useState<Banner[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [turnOffTarget, setTurnOffTarget] = useState<Banner | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Banner | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            setBanners(await bannerService.listBanners());
            setLoadError(null);
        } catch (e) {
            const err = e as Error & { status?: number };
            setLoadError(err.status === 403 ? "You don't have access to Banners." : err.message || "Failed to load banners.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const edit = (b: Banner) => navigate(bannerEditPath(b.uuid));

    async function confirmTurnOff() {
        if (!turnOffTarget) return;
        setBusy(true);
        try {
            await bannerService.turnOffBanner(turnOffTarget.uuid);
            showAlert("Banner turned off.", "success", 3000);
        } catch (e) {
            // banner_not_live / banner_expired: someone else got there first; the reload shows it.
            showAlert((e as Error).message, "error");
        } finally {
            setBusy(false);
            setTurnOffTarget(null);
            load();
        }
    }

    async function confirmDelete() {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            await bannerService.deleteBanner(deleteTarget.uuid);
            showAlert("Banner deleted.", "success", 3000);
        } catch (e) {
            // banner_live: it went Live after the list loaded; the reload shows Turn Off instead.
            showAlert((e as Error).message, "error");
        } finally {
            setBusy(false);
            setDeleteTarget(null);
            load();
        }
    }

    // The database allows at most one Live banner.
    const live = banners.find((b) => b.status === "Live");

    return (
        <div className="banners-page">
            <Message />
            <div className="banners-page__header">
                <div>
                    <h1>Banners</h1>
                    <p className="text-muted small mb-0">
                        Messages shown at the top of the client Partner Dashboard. One banner can be live at a time.
                    </p>
                </div>
                <Button variant="primary" onClick={() => navigate(`${import.meta.env.BASE_URL}${Paths.BANNER_NEW}`)}>
                    + New Banner
                </Button>
            </div>

            {isLoading && banners.length === 0 && !loadError ? (
                <Loader />
            ) : loadError ? (
                <div className="alert alert-danger" role="alert">{loadError}</div>
            ) : (
                <>
                    <LiveNowCard banner={live} onEdit={edit} onTurnOff={setTurnOffTarget} />
                    <BannersTable banners={banners} onEdit={edit} onTurnOff={setTurnOffTarget} onDelete={setDeleteTarget} />
                </>
            )}

            <TurnOffBannerModal
                show={!!turnOffTarget}
                busy={busy}
                message={turnOffTarget?.message ?? ""}
                onCancel={() => setTurnOffTarget(null)}
                onConfirm={confirmTurnOff}
            />
            <ConfirmBannerModal
                show={!!deleteTarget}
                busy={busy}
                title="Delete Banner"
                body={`"${deleteTarget?.message ?? ""}" will be removed from All Banners. This cannot be undone.`}
                actionLabel="Delete"
                onCancel={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
